import type {
    DataTableColumn,
    FilterItemConfig,
    OptionItem,
} from '@/components/ui/data-table-customized'
import {
    createDateFormatter,
    createInternalLinkFormatter,
    createOptionsFormatter,
} from '@/components/ui/data-table-customized'
import {
    CONTRACT_DIRECTION_OPTIONS,
    CONTRACT_ROLE_OPTIONS,
} from '@/modules/Contract/constants'
import { getAccountManagerOptionsPaginated } from '@/modules/Employee/services'
import { getProductsGroupOptions } from '@/modules/ProductGroup/services'
import { TFunction } from 'i18next'
import { Check } from 'lucide-react'
import { SERVICE_STATUS } from '../constants'
import type { Service } from '../types'
import { ServiceStatus } from '../types'

// ============================================================================
// Status Utilities
// ============================================================================

/**
 * Get service status options for filters/display
 */
export const getServiceStatusOptions = (
    t: TFunction,
    filterStatuses?: ServiceStatus[]
): OptionItem[] => {
    const allStatuses = SERVICE_STATUS(t)
    const statuses = filterStatuses
        ? allStatuses.filter((s) => filterStatuses.includes(s.value))
        : allStatuses

    return statuses.map((item) => ({
        label: item.label,
        value: item.value,
        color: item.color as OptionItem['color'],
    }))
}

// ============================================================================
// Column Configuration
// ============================================================================

/**
 * Create service list columns configuration
 */
export const createServiceColumns = (
    t: TFunction
): DataTableColumn<Service>[] => [
    {
        key: 'service_id',
        title: t('service_id'),
        width: 80,
        sortable: true,
        pinned: 'left',
        formatter: createInternalLinkFormatter({
            pathKey: 'serviceDetail',
            pathParams: (value) => ({ id: value }),
        }),
    },
    {
        key: 'product_group_name',
        title: t('product_group_name'),
        width: 250,
        sortable: true,
    },
    {
        key: 'service_name',
        title: t('service_name'),
        width: 400,
        sortable: true,
        cellClassName: 'uppercase',
    },
    {
        key: 'name',
        title: t('entity-name'),
        width: 250,
    },
    {
        key: 'cs_followers',
        title: t('cs_followers'),
        width: 250,
        render: (value) => (value as string[])?.join(', ') || '-',
    },
    {
        key: 'customer_name',
        title: t('service_customer'),
        width: 400,
        sortable: true,
        cellClassName: 'uppercase truncate',
        formatter: createInternalLinkFormatter({
            pathKey: 'customerDetail',
            pathParams: (_, row) => ({ id: row?.customer_id?.toString() }),
            label: (_, row) => row?.company_name || row?.customer_name || '-',
        }),
    },
    {
        key: 'service_status',
        title: t('status'),
        width: 200,
        sortable: true,
        formatter: createOptionsFormatter({
            options: getServiceStatusOptions(t),
        }),
    },
    {
        key: 'contract_role',
        title: t('contract_role'),
        width: 160,
        sortable: false,
        formatter: createOptionsFormatter({
            options: CONTRACT_ROLE_OPTIONS(t) as OptionItem[],
        }),
    },
    {
        key: 'contract_direction',
        title: t('contract_direction'),
        width: 140,
        sortable: false,
        formatter: createOptionsFormatter({
            options: CONTRACT_DIRECTION_OPTIONS(t) as OptionItem[],
        }),
    },
    {
        key: 'service_active_cycling',
        title: t('service_active_cycling'),
        width: 220,
        render: (_, row) => {
            if (!row?.current_startdate && !row?.current_enddate) return '-'
            const start = row?.current_startdate
                ? new Date(row.current_startdate).toLocaleDateString('vi-VN')
                : '-'
            const end = row?.current_enddate
                ? new Date(row.current_enddate).toLocaleDateString('vi-VN')
                : '-'
            return `${start} - ${end}`
        },
    },
    {
        key: 'service_renew_cycling',
        title: t('service_renew_cycling'),
        width: 300,
        render: (_, row) => {
            if (row?.service_status === ServiceStatus.Cancelled) return null
            if (!row?.continue_startdate && !row?.continue_enddate) return '-'
            const start = row?.continue_startdate
                ? new Date(row.continue_startdate).toLocaleDateString('vi-VN')
                : '-'
            const end = row?.continue_enddate
                ? new Date(row.continue_enddate).toLocaleDateString('vi-VN')
                : '-'
            return `${start} - ${end}`
        },
    },
    {
        key: 'service_start_date',
        title: t('service_start_date'),
        width: 150,
        sortable: true,
        formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
    },
    {
        key: 'service_stopped_date',
        title: t('service_stopped_date'),
        width: 150,
        sortable: true,
        formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
    },
    {
        key: 'created_at',
        title: t('created_at'),
        width: 120,
        sortable: true,
        formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
    },
]

/**
 * Create POC service columns with additional fields
 */
export const createPOCServiceColumns = (
    t: TFunction
): DataTableColumn<Service>[] => [
    {
        key: 'service_id',
        title: t('service_id'),
        width: 80,
        sortable: true,
        pinned: 'left',
        formatter: createInternalLinkFormatter({
            pathKey: 'serviceDetail',
            pathParams: (value) => ({ id: value }),
        }),
    },
    {
        key: 'product_group_name',
        title: t('product_group_name'),
        width: 250,
        sortable: true,
        cellClassName: 'uppercase',
    },
    {
        key: 'service_name',
        title: t('service_name'),
        width: 200,
        sortable: true,
    },
    {
        key: 'customer_name',
        title: t('service_customer'),
        width: 300,
        sortable: true,
        cellClassName: 'uppercase',
        formatter: createInternalLinkFormatter({
            pathKey: 'customerDetail',
            pathParams: (_, row) => ({ id: row?.customer_id?.toString() }),
            label: (_, row) => row?.company_name || row?.customer_name || '-',
        }),
    },
    {
        key: 'cs_followers',
        title: t('cs_followers'),
        width: 250,
        render: (value) => (value as string[])?.join(', ') || '-',
    },
    {
        key: 'service_status',
        title: t('status'),
        width: 150,
        sortable: true,
        formatter: createOptionsFormatter({
            options: getServiceStatusOptions(t),
        }),
    },
    {
        key: 'is_paid',
        title: t('poc_is_paid'),
        width: 120,
        headerClassName: 'text-center',
        cellClassName: 'text-center',
        // Nothing for false/missing (field may be absent until the API ships it)
        render: (value) =>
            value ? <Check className="mx-auto h-4 w-4 text-green-600" /> : null,
    },
    {
        key: 'service_response_poc',
        title: t('service_response_poc'),
        width: 200,
        render: (value) => (value as string[])?.join(', ') || '-',
    },
    {
        key: 'service_retention_poc',
        title: t('service_retention_poc'),
        width: 200,
        render: (value) => (value as string[])?.join(', ') || '-',
    },
    {
        key: 'service_start_date',
        title: t('service_start_date'),
        width: 150,
        sortable: true,
        formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
    },
    {
        key: 'service_stopped_date',
        title: t('service_stopped_date'),
        width: 150,
        sortable: true,
        formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
    },
    {
        key: 'created_at',
        title: t('created_at'),
        width: 130,
        sortable: true,
        formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
    },
]

/**
 * Create statement (reconciliation) service columns.
 * Shows the current billing period as the "statement period" plus status.
 * The Actions (Rollback) column is provided by the DataTable `actions` prop,
 * not here.
 */
export const createStatementColumns = (
    t: TFunction
): DataTableColumn<Service>[] => [
    {
        key: 'service_id',
        title: t('service_id'),
        width: 80,
        sortable: true,
        pinned: 'left',
        formatter: createInternalLinkFormatter({
            pathKey: 'serviceDetail',
            pathParams: (value) => ({ id: value }),
        }),
    },
    {
        key: 'product_group_name',
        title: t('product_group_name'),
        width: 220,
        sortable: true,
    },
    {
        key: 'service_name',
        title: t('service_name'),
        width: 320,
        sortable: true,
        cellClassName: 'uppercase',
    },
    {
        key: 'customer_name',
        title: t('service_customer'),
        width: 320,
        sortable: true,
        cellClassName: 'uppercase truncate',
        formatter: createInternalLinkFormatter({
            pathKey: 'customerDetail',
            pathParams: (_, row) => ({ id: row?.customer_id?.toString() }),
            label: (_, row) => row?.company_name || row?.customer_name || '-',
        }),
    },
    {
        key: 'statement_period',
        title: t('statement-period'),
        width: 240,
        render: (_, row) => {
            if (!row?.statement_startdate && !row?.statement_enddate) return '-'
            const start = row?.statement_startdate
                ? new Date(row.statement_startdate).toLocaleDateString('vi-VN')
                : '-'
            const end = row?.statement_enddate
                ? new Date(row.statement_enddate).toLocaleDateString('vi-VN')
                : '-'
            return `${start} - ${end}`
        },
    },
    {
        // For an already-reconciled service (has an active snapshot), show what a
        // rollback would do: current statement cycle → the cycle it reverts to.
        key: 'rollback_preview',
        title: t('rollback-preview'),
        width: 300,
        render: (_, row) => {
            if (!row?.has_active_snapshot) return '-'
            const fmt = (s?: string | null, e?: string | null) => {
                const start = s ? new Date(s).toLocaleDateString('vi-VN') : '-'
                const end = e ? new Date(e).toLocaleDateString('vi-VN') : '-'
                return `${start} - ${end}`
            }
            const current = fmt(
                row?.statement_startdate,
                row?.statement_enddate
            )
            const revertTo = fmt(
                row?.rollback_to_statement_startdate,
                row?.rollback_to_statement_enddate
            )
            return `${current} → ${revertTo}`
        },
    },
    {
        key: 'service_status',
        title: t('status'),
        width: 180,
        sortable: true,
        formatter: createOptionsFormatter({
            options: getServiceStatusOptions(t),
        }),
    },
]

// ============================================================================
// Filter Configuration
// ============================================================================

/**
 * Create service list filters configuration
 */
export const createServiceFilters = (
    t: TFunction,
    options?: {
        serviceStatuses?: ServiceStatus[]
        showAccountManager?: boolean
        showDateFilters?: boolean
        showContractFilters?: boolean
    }
): FilterItemConfig[] => {
    const {
        serviceStatuses,
        showAccountManager = true,
        showDateFilters = true,
        showContractFilters = true,
    } = options || {}

    const filters: FilterItemConfig[] = [
        {
            name: 'service_status',
            type: 'multi-select',
            label: t('status'),
            options:
                getServiceStatusOptions(t, serviceStatuses)?.map((item) => ({
                    label: item.label,
                    value: item.value.toString(),
                })) || [],
            placeholder: t('select-status'),
        },
        {
            name: 'product_group_name',
            type: 'async-select',
            label: t('product_group_name'),
            asyncOptions: getProductsGroupOptions,
            multiple: true,
            placeholder: t('select-product-group'),
        },
    ]

    if (showContractFilters) {
        filters.push(
            {
                name: 'contract_role',
                type: 'multi-select',
                label: t('contract_role'),
                placeholder: t('contract_role'),
                options: CONTRACT_ROLE_OPTIONS(t),
            },
            {
                name: 'contract_direction',
                type: 'multi-select',
                label: t('contract_direction'),
                placeholder: t('contract_direction'),
                options: CONTRACT_DIRECTION_OPTIONS(t),
            }
        )
    }

    if (showAccountManager) {
        filters.push({
            name: 'account_manager_codes',
            type: 'async-select',
            label: t('account_manager_codes'),
            asyncOptions: async (query, page) => {
                const result = await getAccountManagerOptionsPaginated(
                    query,
                    page
                )
                // Prepend "no CS assignee" option on first page
                if (page === 1 && !query) {
                    const noAssigneeOption = {
                        label: t('no-cs-assignee'),
                        value: '-1',
                    }
                    return {
                        ...result,
                        options: [noAssigneeOption, ...result.options],
                    }
                }
                return result
            },
            multiple: true,
            placeholder: t('select-account-manager'),
        })
    }

    if (showDateFilters) {
        filters.push(
            {
                name: 'service_start_date',
                type: 'daterangepicker',
                names: ['service_start_date', 'service_start_date_to'],
                label: t('service_start_date'),
                placeholder: t('select-date-range'),
            },
            {
                name: 'service_stopped_date',
                type: 'daterangepicker',
                names: ['service_stopped_date', 'service_stopped_date_to'],
                label: t('service_stopped_date'),
                placeholder: t('select-date-range'),
            },
            {
                name: 'service_created_date',
                type: 'daterangepicker',
                names: ['service_created_date', 'service_created_date_to'],
                label: t('service_created_date'),
                placeholder: t('select-date-range'),
            }
        )
    }

    return filters
}

// ============================================================================
// Constants
// ============================================================================

export const SERVICE_ALLOWED_SORT_FIELDS = [
    'service_id',
    'service_name',
    'service_start_date',
    'service_stopped_date',
    'service_status',
    'customer_name',
    'product_group_name',
    'created_at',
]

export const SERVICE_DEFAULT_SORT = {
    sort_by: 'service_status',
    sort_order: 'desc',
} as const

export interface ServiceFilters {
    service_status: string[]
    contract_role: string[]
    contract_direction: string[]
    product_group_name: string[]
    account_manager_codes: string[]
    service_start_date: string[]
    service_start_date_to: string
    service_stopped_date: string[]
    service_stopped_date_to: string
    service_created_date: string[]
    service_created_date_to: string
}

export const SERVICE_FILTER_DEFAULTS: ServiceFilters = {
    service_status: [],
    contract_role: [],
    contract_direction: [],
    product_group_name: [],
    account_manager_codes: [],
    service_start_date: [],
    service_start_date_to: '',
    service_stopped_date: [],
    service_stopped_date_to: '',
    service_created_date: [],
    service_created_date_to: '',
}

// POC specific statuses
export const POC_SERVICE_STATUSES = [
    ServiceStatus.Trialing,
    ServiceStatus.WaitingConfirm,
    ServiceStatus.Rejected,
    ServiceStatus.Resubmit,
    ServiceStatus.Cancelled,
    ServiceStatus.WaitingCreateService,
    ServiceStatus.PendingTerminated,
]

// All service statuses (full list for main tab)
export const ALL_SERVICE_STATUSES = [
    ServiceStatus.WaitingConfirm,
    ServiceStatus.WaitingCreateService,
    ServiceStatus.Active,
    ServiceStatus.PendingTerminated,
    ServiceStatus.Cancelled,
]

// Waiting confirm statuses
export const WAITING_CONFIRM_STATUSES = [
    ServiceStatus.WaitingConfirm,
    ServiceStatus.Rejected,
    ServiceStatus.Resubmit,
]

// Device liquidation statuses
export const DEVICE_LIQUIDATION_STATUSES = [
    ServiceStatus.WaitingConfirm,
    ServiceStatus.Rejected,
    ServiceStatus.Resubmit,
]

// Statement (reconciliation) statuses — active services are the ones with a
// billing cycle that can be reconciled / rolled back.
export const STATEMENT_SERVICE_STATUSES = [
    ServiceStatus.Active,
    ServiceStatus.PendingTerminated,
]
